import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import CustomerHeader from '../../components/ui/CustomerHeader';
import Footer from '../../components/ui/Footer';
import Image from '../../components/AppImage';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useWishlist } from '../../context/WishlistContext';
import { useCart } from '../../context/CartContext';

const Wishlist = () => {
  const { items, remove, clear } = useWishlist();
  const cart = useCart();

  // Self-heal: any wishlist item stored without an image (legacy adds before
  // the detail page populated the image field) gets backfilled from the
  // storefront product feed by id.
  const API_BASE    = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_STOREFRONT_API_URL) || 'https://dashboard.offloadhost.com';
  const CUSTOMER_ID = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_CUSTOMER_ID) || '';
  const [imageById, setImageById] = useState({});
  useEffect(() => {
    if (!CUSTOMER_ID) return;
    if (!items.some((it) => !it.image)) return;  // nothing to backfill
    const ctl = new AbortController();
    fetch(`${API_BASE}/storefront/${CUSTOMER_ID}/products`, { signal: ctl.signal })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        const map = {};
        (data?.products || []).forEach((p) => { map[String(p.id)] = p.image_url || ''; });
        setImageById(map);
      })
      .catch(() => {});
    return () => ctl.abort();
  }, [items.length]);

  const imageFor = (item) => item.image || imageById[String(item.id)] || '';

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <CustomerHeader cartItemCount={cart.totalCount} />

      <div className="pt-16 flex-1">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-heading font-semibold text-foreground">
              Your Wishlist {items.length > 0 && <span className="text-muted-foreground text-xl">({items.length})</span>}
            </h1>
            {items.length > 0 && (
              <button
                onClick={clear}
                className="text-sm text-muted-foreground hover:text-destructive transition-colors"
              >
                Clear all
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <div className="bg-card border border-border rounded-2xl p-12 text-center">
              <Icon name="Heart" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h2 className="text-lg font-medium text-foreground mb-1">No saved items yet</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Tap the heart on any product you love and it'll show up here.
              </p>
              <Link
                to="/product-catalog"
                className="inline-flex items-center gap-2 text-primary hover:underline"
              >
                <Icon name="Store" size={16} />
                Browse the shop
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((item) => (
                <div key={item.id} className="bg-card border border-border rounded-xl overflow-hidden flex flex-col">
                  <Link to={`/product-details?id=${item.id}`} className="block aspect-square bg-muted">
                    {imageFor(item) && (
                      <Image src={imageFor(item)} alt={item.name} className="w-full h-full object-cover" />
                    )}
                  </Link>
                  <div className="p-4 flex-1 flex flex-col">
                    <Link to={`/product-details?id=${item.id}`} className="text-foreground font-medium hover:text-primary line-clamp-2 mb-1">
                      {item.name}
                    </Link>
                    <p className="text-lg font-bold text-foreground mb-3">${(item.price ?? 0).toFixed(2)}</p>
                    <div className="mt-auto flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1"
                        iconName="ShoppingCart"
                        iconPosition="left"
                        onClick={() => cart.addItem(item, 1)}
                      >
                        Add to Cart
                      </Button>
                      <button
                        onClick={() => remove(item.id)}
                        aria-label="Remove from wishlist"
                        className="w-9 h-9 rounded-md border border-input flex items-center justify-center text-muted-foreground hover:text-destructive hover:border-destructive transition-colors"
                      >
                        <Icon name="X" size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Wishlist;
